import { Box, Button, Flex, HStack, Icon, Link, Text } from '@chakra-ui/react'
import { Section } from 'commons/components/layout/Section'
import { BgColors, TextColors } from 'commons/design-system/tokens'
import {
	buildQrDeeplink,
	type QrCodeAppProvider,
	type QrCodeContext
} from 'commons/utils/qrCodeDeeplinkHelpers'
import { QRCodeSVG } from 'qrcode.react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MdOpenInNew } from 'react-icons/md'

type QRCodeAppProps = {
	context: QrCodeContext
	identifier: string
	amount?: string
	description?: string
	lydiaLogoUrl: string
	sumeriaLogoUrl: string
}

const QR_CODE_PROVIDERS = ['lydia', 'sumeria'] as const

const QRCodeApp = ({
	context,
	identifier,
	amount,
	description,
	lydiaLogoUrl,
	sumeriaLogoUrl
}: QRCodeAppProps) => {
	const { t } = useTranslation()
	const [provider, setProvider] = useState<QrCodeAppProvider>('lydia')

	const qrCodeConfigs = useMemo(
		() =>
			QR_CODE_PROVIDERS.map((appProvider) => ({
				provider: appProvider,
				deeplink: buildQrDeeplink({
					context,
					provider: appProvider,
					identifier,
					amount,
					description
				}),
				logoUrl: appProvider === 'lydia' ? lydiaLogoUrl : sumeriaLogoUrl
			})),
		[context, identifier, amount, description, lydiaLogoUrl, sumeriaLogoUrl]
	)

	const deeplink = qrCodeConfigs.find((config) => config.provider === provider)?.deeplink ?? ''

	useEffect(() => {
		qrCodeConfigs.forEach(({ logoUrl }) => {
			const img = new Image()
			img.src = logoUrl
		})
	}, [qrCodeConfigs])

	const scanHintKey =
		context === 'payment' ? 'qr_code.scan_hint_payment' : 'qr_code.scan_hint_money_pot'

	return (
		<Box display='flex' w='full'>
			<Section>
				<Flex flexDirection='column' alignItems='center' gap={3} py={2}>
					<Text fontWeight='600' fontSize='md' textAlign='center'>
						{t(scanHintKey)}
					</Text>
					<Box
						bg={BgColors.Z5}
						border='1px solid'
						borderColor={BgColors.Z3}
						borderRadius='md'
						px={6}
						py={4}
						w='full'
						maxW='320px'
					>
						<Flex flexDirection='column' alignItems='center' gap={4}>
							<Link
								href={deeplink}
								target='_blank'
								rel='noopener noreferrer'
								display={{ base: 'inline-flex', md: 'none' }}
								alignItems='center'
								gap={1}
								color={TextColors.HIGHLIGHT_ACTION}
								fontWeight='600'
								fontSize='sm'
								_hover={{ textDecoration: 'underline' }}
							>
								{t('qr_code.open_link')}
								<Icon as={MdOpenInNew} boxSize='16px' />
							</Link>
							<Box position='relative' w='180px' h='180px'>
								{qrCodeConfigs.map(({ provider: appProvider, deeplink: qrDeeplink, logoUrl }) => {
									const isSelected = provider === appProvider
									return (
										<Box
											key={appProvider}
											position='absolute'
											top={0}
											left={0}
											visibility={isSelected ? 'visible' : 'hidden'}
											aria-hidden={!isSelected}
										>
											<QRCodeSVG
												value={qrDeeplink}
												size={180}
												level='H'
												role='img'
												title={isSelected ? t(scanHintKey) : undefined}
												aria-label={isSelected ? t(scanHintKey) : undefined}
												imageSettings={{
													src: logoUrl,
													width: 40,
													height: 40,
													excavate: false
												}}
											/>
										</Box>
									)
								})}
							</Box>
							<HStack gap={2} w='full'>
								{QR_CODE_PROVIDERS.map((appProvider) => {
									const isSelected = provider === appProvider
									return (
										<Button
											key={appProvider}
											type='button'
											flex='1'
											borderRadius='full'
											border='1px solid'
											borderColor={isSelected ? TextColors.HIGHLIGHT_ACTION : BgColors.Z3}
											bg={isSelected ? BgColors.HIGHLIGHT_Z1 : BgColors.Z4}
											color={isSelected ? TextColors.HIGHLIGHT_ACTION : TextColors.SECONDARY}
											fontWeight='600'
											fontSize='sm'
											py={2}
											h='auto'
											variant='ghost'
											onClick={() => setProvider(appProvider)}
										>
											{t(`qr_code.${appProvider}`)}
										</Button>
									)
								})}
							</HStack>
						</Flex>
					</Box>
					<Text fontWeight='500' fontSize='sm' textAlign='center'>
						{t('qr_code.or')}
					</Text>
					<Text fontWeight='600' fontSize='md' textAlign='center'>
						{t('qr_code.pay_with_lydia_ask_payment_hint')}
					</Text>
				</Flex>
			</Section>
		</Box>
	)
}

export default QRCodeApp
